import React, { useState, useEffect, useMemo } from 'react';
import { Activity, RefreshCw, Copy, Check, Cpu, Zap, ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';
import { METRICS_URL } from '../../config';

// Парсинг Prometheus text format
function parsePrometheus(text) {
    const result = {
        process: {},
        system: {},  // память/диск/нагрузка: текущее / всего
        httpTotal: [],      // { method, path, status_class, value }
        durationCount: [],  // { method, path, value } для _count
        durationSum: [],    // { method, path, value } для _sum
    };
    const lines = text.split('\n');
    let currentName = '';
    for (const line of lines) {
        if (line.startsWith('# TYPE ')) {
            currentName = line.split(/\s+/)[2];
            continue;
        }
        if (!line.length || line.startsWith('#')) continue;
        const match = line.match(/^([a-zA-Z_][a-zA-Z0-9_]*)(?:\{(.*)\})?\s+(.+)$/);
        if (!match) continue;
        const [, name, labelsStr, valueStr] = match;
        const value = parseFloat(valueStr);
        if (isNaN(value)) continue;

        if (name === 'app_process_virtual_memory_bytes') result.process.virtualBytes = value;
        else if (name === 'app_process_resident_memory_bytes') result.process.residentBytes = value;
        else if (name === 'app_process_start_time_seconds') result.process.startTime = value;
        else if (name === 'app_process_cpu_seconds_total') result.process.cpuSeconds = value;
        else if (name === 'app_process_open_fds') result.process.openFds = value;
        else if (name === 'app_process_max_fds') result.process.maxFds = value;
        else if (name === 'app_system_memory_used_bytes') result.system.memoryUsedBytes = value;
        else if (name === 'app_system_memory_total_bytes') result.system.memoryTotalBytes = value;
        else if (name === 'app_system_disk_used_bytes') result.system.diskUsedBytes = value;
        else if (name === 'app_system_disk_total_bytes') result.system.diskTotalBytes = value;
        else if (name === 'app_system_load_1') result.system.load1 = value;
        else if (name === 'app_system_load_5') result.system.load5 = value;
        else if (name === 'app_system_load_15') result.system.load15 = value;
        else if (name === 'app_system_cpu_count') result.system.cpuCount = value;
        else if (name === 'app_http_requests_total' && labelsStr) {
            const labels = {};
            labelsStr.replace(/(\w+)="([^"]*)"/g, (_, k, v) => { labels[k] = v; });
            result.httpTotal.push({ method: labels.method, path: labels.path, status_class: labels.status_class, value });
        } else if (name === 'app_http_request_duration_seconds_count' && labelsStr) {
            const labels = {};
            labelsStr.replace(/(\w+)="([^"]*)"/g, (_, k, v) => { labels[k] = v; });
            result.durationCount.push({ method: labels.method, path: labels.path, value });
        } else if (name === 'app_http_request_duration_seconds_sum' && labelsStr) {
            const labels = {};
            labelsStr.replace(/(\w+)="([^"]*)"/g, (_, k, v) => { labels[k] = v; });
            result.durationSum.push({ method: labels.method, path: labels.path, value });
        }
    }
    return result;
}

function formatBytes(bytes) {
    if (bytes == null) return '—';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(1)} МБ`;
}

function formatBytesGB(bytes) {
    if (bytes == null) return '—';
    const gb = bytes / (1024 * 1024 * 1024);
    return gb >= 1 ? `${gb.toFixed(2)} ГБ` : `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
}

function formatCurrentTotal(current, total, formatter = formatBytesGB) {
    const c = formatter(current);
    const t = formatter(total);
    if (c === '—' || t === '—') return '—';
    return `${c} / ${t}`;
}

function formatUptime(epochSeconds) {
    if (epochSeconds == null) return '—';
    const now = Date.now() / 1000;
    const sec = Math.max(0, Math.floor(now - epochSeconds));
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    if (h > 0) return `${h} ч ${m} мин`;
    if (m > 0) return `${m} мин ${s} сек`;
    return `${s} сек`;
}

const MetricsPage = () => {
    const [raw, setRaw] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [copied, setCopied] = useState(false);
    const [showRaw, setShowRaw] = useState(false);
    const [showHelp, setShowHelp] = useState(false);

    const load = async () => {
        try {
            setLoading(true);
            setError(null);
            const r = await fetch(METRICS_URL, { credentials: 'include' });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const text = await r.text();
            setRaw(text);
        } catch (e) {
            setError(e.message || 'Не удалось загрузить метрики');
            setRaw('');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const parsed = useMemo(() => (raw ? parsePrometheus(raw) : null), [raw]);

    const httpByPath = useMemo(() => {
        if (!parsed || !parsed.httpTotal.length) return [];
        const byKey = {};
        for (const { method, path, status_class, value } of parsed.httpTotal) {
            const key = `${method} ${path}`;
            if (!byKey[key]) byKey[key] = { method, path, total: 0, '2xx': 0, '4xx': 0, '5xx': 0 };
            byKey[key].total += value;
            if (status_class) byKey[key][status_class] = (byKey[key][status_class] || 0) + value;
        }
        const sumByPath = {};
        const sumDuration = {};
        for (const { method, path, value } of parsed.durationSum) {
            const key = `${method} ${path}`;
            sumDuration[key] = value;
        }
        for (const { method, path, value } of parsed.durationCount) {
            const key = `${method} ${path}`;
            const sum = sumDuration[key] || 0;
            sumByPath[key] = value > 0 ? (sum / value * 1000).toFixed(0) : '—';
        }
        return Object.values(byKey)
            .map(row => ({
                ...row,
                avgMs: sumByPath[`${row.method} ${row.path}`] || '—',
            }))
            .sort((a, b) => b.total - a.total);
    }, [parsed]);

    const totalRequests = parsed?.httpTotal?.reduce((acc, r) => acc + r.value, 0) ?? 0;
    const status2xx = parsed?.httpTotal?.filter(r => r.status_class === '2xx').reduce((acc, r) => acc + r.value, 0) ?? 0;
    const status4xx = parsed?.httpTotal?.filter(r => r.status_class === '4xx').reduce((acc, r) => acc + r.value, 0) ?? 0;
    const status5xx = parsed?.httpTotal?.filter(r => r.status_class === '5xx').reduce((acc, r) => acc + r.value, 0) ?? 0;

    const copyToClipboard = () => {
        if (!raw) return;
        navigator.clipboard.writeText(raw).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const lines = raw ? raw.split('\n') : [];

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="flex items-center gap-3">
                    <Activity className="text-slate-800" size={28} />
                    <h2 className="text-2xl font-bold text-slate-800">Метрики нагрузки</h2>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 flex justify-center">
                    <div className="flex items-center gap-2 text-slate-500">
                        <RefreshCw size={20} className="animate-spin" />
                        Загрузка метрик...
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="space-y-6">
                <div className="flex items-center gap-3">
                    <Activity className="text-slate-800" size={28} />
                    <h2 className="text-2xl font-bold text-slate-800">Метрики нагрузки</h2>
                </div>
                <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 text-rose-700">{error}</div>
                <button onClick={load} className="px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700">Повторить</button>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-3">
                    <Activity className="text-slate-800" size={28} />
                    <h2 className="text-2xl font-bold text-slate-800">Метрики нагрузки backend</h2>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setShowHelp(!showHelp)}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
                    >
                        <HelpCircle size={18} /> Какие метрики нужны
                    </button>
                    <button onClick={copyToClipboard} className="flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50">
                        {copied ? <Check size={18} className="text-green-600" /> : <Copy size={18} />}
                        {copied ? 'Скопировано' : 'Копировать'}
                    </button>
                    <button onClick={load} disabled={loading} className="flex items-center gap-2 px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 disabled:opacity-50">
                        <RefreshCw size={18} className={loading ? 'animate-spin' : ''} /> Обновить
                    </button>
                </div>
            </div>

            {showHelp && (
                <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 text-sm text-slate-700 space-y-2">
                    <p className="font-semibold text-indigo-800">Зачем нужны эти метрики</p>
                    <ul className="list-disc list-inside space-y-1">
                        <li><strong>Система (память / диск / нагрузка)</strong> — данные с машины, где запущен backend: в Docker — контейнер, на VPS без Docker — сервер. Формат «текущее / всего».</li>
                        <li><strong>Процесс (вирт. / RSS)</strong> — потребление только процессом backend; рост RSS — утечки.</li>
                        <li><strong>CPU (время)</strong> — сколько всего процессорного времени потратил процесс.</li>
                        <li><strong>Открытые FD / макс. FD</strong> — близко к максимуму → риск «too many open files».</li>
                        <li><strong>HTTP: запросы по путям</strong> — нагрузка по эндпоинтам; 4xx/5xx — ошибки.</li>
                        <li><strong>Среднее время ответа (мс)</strong> — задержка по эндпоинтам.</li>
                    </ul>
                    <p className="text-slate-600 pt-1">Prometheus забирает метрики с /metrics и строит графики в Grafana; здесь — срез «здесь и сейчас».</p>
                </div>
            )}

            {/* Система (память / диск / нагрузка): текущее / всего */}
            {parsed?.system && (parsed.system.memoryTotalBytes != null || parsed.system.diskTotalBytes != null || parsed.system.load1 != null) && (
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                    <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex items-center gap-2">
                        <Activity size={18} className="text-slate-600" />
                        <span className="font-medium text-slate-700">Система (VPS / контейнер)</span>
                    </div>
                    <div className="p-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                        {(parsed.system.memoryUsedBytes != null || parsed.system.memoryTotalBytes != null) && (
                            <div className="p-3 rounded-lg bg-slate-50">
                                <p className="text-xs text-slate-500 uppercase tracking-wide">Память</p>
                                <p className="text-lg font-semibold text-slate-800">
                                    {formatCurrentTotal(parsed.system.memoryUsedBytes, parsed.system.memoryTotalBytes)}
                                </p>
                                <p className="text-xs text-slate-500">использовано / всего</p>
                            </div>
                        )}
                        {(parsed.system.diskUsedBytes != null || parsed.system.diskTotalBytes != null) && (
                            <div className="p-3 rounded-lg bg-slate-50">
                                <p className="text-xs text-slate-500 uppercase tracking-wide">Диск (корень)</p>
                                <p className="text-lg font-semibold text-slate-800">
                                    {formatCurrentTotal(parsed.system.diskUsedBytes, parsed.system.diskTotalBytes)}
                                </p>
                                <p className="text-xs text-slate-500">использовано / всего</p>
                            </div>
                        )}
                        {(parsed.system.load1 != null || parsed.system.load5 != null) && (
                            <div className="p-3 rounded-lg bg-slate-50">
                                <p className="text-xs text-slate-500 uppercase tracking-wide">Нагрузка (load average)</p>
                                <p className="text-lg font-semibold text-slate-800">
                                    {parsed.system.load1 != null && parsed.system.load5 != null && parsed.system.load15 != null
                                        ? `${Number(parsed.system.load1).toFixed(2)} / ${Number(parsed.system.load5).toFixed(2)} / ${Number(parsed.system.load15).toFixed(2)}`
                                        : (parsed.system.load1 != null ? Number(parsed.system.load1).toFixed(2) : '—')}
                                </p>
                                <p className="text-xs text-slate-500">
                                    {parsed.system.cpuCount != null ? `1 / 5 / 15 мин · ${Math.round(parsed.system.cpuCount)} ядер` : '1 / 5 / 15 мин'}
                                </p>
                            </div>
                        )}
                        {parsed.system.cpuCount != null && (
                            <div className="p-3 rounded-lg bg-slate-50">
                                <p className="text-xs text-slate-500 uppercase tracking-wide">Ядер CPU</p>
                                <p className="text-lg font-semibold text-slate-800">{Math.round(parsed.system.cpuCount)}</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Процесс и ресурсы */}
            {parsed?.process && (
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                    <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex items-center gap-2">
                        <Cpu size={18} className="text-slate-600" />
                        <span className="font-medium text-slate-700">Процесс и ресурсы</span>
                    </div>
                    <div className="p-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
                        <div className="p-3 rounded-lg bg-slate-50">
                            <p className="text-xs text-slate-500 uppercase tracking-wide">Память (вирт.)</p>
                            <p className="text-lg font-semibold text-slate-800">{formatBytes(parsed.process.virtualBytes)}</p>
                        </div>
                        <div className="p-3 rounded-lg bg-slate-50">
                            <p className="text-xs text-slate-500 uppercase tracking-wide">Память (RSS)</p>
                            <p className="text-lg font-semibold text-slate-800">{formatBytes(parsed.process.residentBytes)}</p>
                        </div>
                        <div className="p-3 rounded-lg bg-slate-50">
                            <p className="text-xs text-slate-500 uppercase tracking-wide">CPU (всего)</p>
                            <p className="text-lg font-semibold text-slate-800">{parsed.process.cpuSeconds != null ? `${Number(parsed.process.cpuSeconds).toFixed(1)} с` : '—'}</p>
                        </div>
                        <div className="p-3 rounded-lg bg-slate-50">
                            <p className="text-xs text-slate-500 uppercase tracking-wide">Открыто FD</p>
                            <p className="text-lg font-semibold text-slate-800">{parsed.process.openFds != null ? Math.round(parsed.process.openFds) : '—'}</p>
                        </div>
                        <div className="p-3 rounded-lg bg-slate-50">
                            <p className="text-xs text-slate-500 uppercase tracking-wide">Макс. FD</p>
                            <p className="text-lg font-semibold text-slate-800">{parsed.process.maxFds != null ? Math.round(parsed.process.maxFds) : '—'}</p>
                        </div>
                        <div className="p-3 rounded-lg bg-slate-50">
                            <p className="text-xs text-slate-500 uppercase tracking-wide">Время работы</p>
                            <p className="text-lg font-semibold text-slate-800">{formatUptime(parsed.process.startTime)}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* HTTP: сводка по статусам */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex items-center gap-2">
                    <Zap size={18} className="text-slate-600" />
                    <span className="font-medium text-slate-700">HTTP-запросы</span>
                </div>
                <div className="p-4">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                        <div className="p-3 rounded-lg bg-slate-50">
                            <p className="text-xs text-slate-500 uppercase tracking-wide">Всего запросов</p>
                            <p className="text-2xl font-bold text-slate-800">{totalRequests}</p>
                        </div>
                        <div className="p-3 rounded-lg bg-green-50">
                            <p className="text-xs text-green-600 uppercase tracking-wide">Успешные (2xx)</p>
                            <p className="text-2xl font-bold text-green-700">{status2xx}</p>
                        </div>
                        <div className="p-3 rounded-lg bg-amber-50">
                            <p className="text-xs text-amber-600 uppercase tracking-wide">Ошибки клиента (4xx)</p>
                            <p className="text-2xl font-bold text-amber-700">{status4xx}</p>
                        </div>
                        <div className="p-3 rounded-lg bg-rose-50">
                            <p className="text-xs text-rose-600 uppercase tracking-wide">Ошибки сервера (5xx)</p>
                            <p className="text-2xl font-bold text-rose-700">{status5xx}</p>
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead>
                                <tr className="border-b border-slate-200 text-left text-slate-600">
                                    <th className="py-2 pr-4">Метод</th>
                                    <th className="py-2 pr-4">Путь</th>
                                    <th className="py-2 pr-4 text-right">Запросов</th>
                                    <th className="py-2 pr-4 text-right">2xx</th>
                                    <th className="py-2 pr-4 text-right">4xx / 5xx</th>
                                    <th className="py-2 pr-4 text-right">Ср. время (мс)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {httpByPath.slice(0, 20).map((row, i) => (
                                    <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                                        <td className="py-2 pr-4 font-mono text-slate-700">{row.method}</td>
                                        <td className="py-2 pr-4 font-mono text-slate-800 break-all">{row.path}</td>
                                        <td className="py-2 pr-4 text-right font-medium">{row.total}</td>
                                        <td className="py-2 pr-4 text-right text-green-600">{row['2xx'] ?? '—'}</td>
                                        <td className="py-2 pr-4 text-right">
                                            {(row['4xx'] || row['5xx']) ? (
                                                <span className="text-amber-600">{(row['4xx'] || 0) + (row['5xx'] || 0)}</span>
                                            ) : '—'}
                                        </td>
                                        <td className="py-2 pr-4 text-right font-mono">{row.avgMs}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Сырой вывод */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                <button
                    onClick={() => setShowRaw(!showRaw)}
                    className="w-full px-4 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between text-left hover:bg-slate-100"
                >
                    <span className="text-sm font-medium text-slate-600">Сырой вывод Prometheus (/metrics)</span>
                    {showRaw ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </button>
                {showRaw && (
                    <div className="p-4 overflow-x-auto max-h-[50vh] overflow-y-auto">
                        <pre className="text-xs font-mono text-slate-700 whitespace-pre break-all">
                            {lines.map((line, i) => {
                                if (line.startsWith('# HELP ') || line.startsWith('# TYPE ')) {
                                    return <span key={i} className="text-slate-500">{line}\n</span>;
                                }
                                if (line.startsWith('#')) return <span key={i} className="text-slate-400">{line}\n</span>;
                                const nameMatch = line.match(/^([a-zA-Z_][a-zA-Z0-9_]*)/);
                                if (nameMatch) {
                                    return (
                                        <span key={i}>
                                            <span className="text-indigo-600 font-medium">{nameMatch[1]}</span>
                                            <span className="text-slate-700">{line.slice(nameMatch[1].length)}\n</span>
                                        </span>
                                    );
                                }
                                return <span key={i} className="text-slate-700">{line}\n</span>;
                            })}
                        </pre>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MetricsPage;
